import React from 'react';
import { makeStyles } from '@material-ui/core';
import { TableCell, TableRow } from '@material-ui/core';
import TablePaginationActions from '../CoreComponents/TablePaginationActions';
import { handleRowsPerPageChange } from '../utils/PaginationUtils';
import { TablePagination } from '@material-ui/core';
import useAbortableFetch from '../../hooks/useAbortableFetch';
import { useState, useEffect } from 'react';
import { CONSUMPTION_DATA_DEFAULT_ROWS_PER_PAGE, CONSUMPTION_DATA_ROWS_PER_PAGE_OPTIONS } from '../utils/PaginationUtils';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import DoneIcon from '@material-ui/icons/Done';
import CloseIcon from '@material-ui/icons/Close';
import { BrandAutocomplete } from '../CoreComponents/BrandAutoComplete';
import { useSnackbarAlert } from '../../hooks/useSnackbarAlert.js';
import { BrandButton } from '../CoreComponents/BrandButton.js';
import { ButtonSize } from '../../constants/buttonConstants.js';
import AddRoundedIcon from '@material-ui/icons/AddRounded';
import { BrandPopover } from '../CoreComponents/BrandPopover';
import { BrandTooltip, TooltipType } from '../CoreComponents/BrandTooltip';
import { BrandCheckbox } from '../CoreComponents/BrandCheckbox';

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    width: '100%',
    maxWidth: '100%'
  },
  tableCell: {
    borderBottom: 'none',
    borderRadius: '6px',
    display: 'flex',
    flexDirection: 'row',
    width: '50%'
  },
  tableCellLast: {
    borderBottom: 'none',
    width: theme.spacing(4),
  },
  tableHeadContainer: {
    backgroundColor: theme.palette.background.light,
    borderRadius: '6px',
    border: `1px solid ${theme.palette.primary.borderStroke}`,
    marginBottom: '8px',
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
  },
  tableRow: {
    display: 'flex',
    width: '100%'
  },
  tableBodyContainer: {
    backgroundColor: theme.palette.background.dark,
    width: '100%'
  },
  tableCellBody: {
    backgroundColor: theme.palette.background.dark,
    border: 'none',
    paddingLeft: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'space-around',
    width: '50%',
    overflow: 'hidden',
  },
  tableCellBodyEdit: {
    backgroundColor: theme.palette.background.dark, 
    border: 'none',
    paddingLeft: theme.spacing(2),
    padding: '0px',
    display: 'flex',
    flexDirection: 'column',
    width: '50%',
  },
  tableCellBodyLast: {
    backgroundColor: theme.palette.background.dark, 
    border: 'none',
    display: 'flex',
    flexDirection: 'column',
  },
  tableRowBody: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    width: '100%',
    height: '58px',
    display: 'flex',
    backgroundColor: theme.palette.background.dark,
    border: `1px solid ${theme.palette.primary.borderStroke}`,
    borderRadius: '6px',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s ease-in-out',
    '&:hover': {
      borderColor: theme.palette.primary.main
    }
  },
  tableRowBodyChecked: {
    borderColor: theme.palette.primary.main
  },
  iconContainer: {
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    padding: '0 16px'
  },
  rowWrapper: {
    marginBottom: '8px',
    width: '100%',
    display: 'flex'
  },
  consumptionDataContainer: {
    width: '100%'
  },
  tablePagination: {
    '& .MuiSelect-icon': {
        color: 'white'
    }
  },
  actionBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  disabledActionBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    cursor: 'not-allowed',
    color: theme.palette.primary.disabled,
  },
  saveIcon: {
    color: theme.palette.primary.main,
    cursor: 'pointer'
  },
  cancelIcon: {
    color: theme.palette.primary.alert,
    cursor: 'pointer'
  },
  actionBtnContainer: {
    display: 'flex',
    gap: theme.spacing(1)
  },
  halfWidthAutocomplete: {
    width: '50%',
    maxWidth: '50%',
    display: 'inline-block',
    padding: '0px'
  },
  autoCompleteContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  truncatedText: {
    display: 'block',
    width: '100%',
    maxWidth: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  tableMultiEditCheckbox: {
    paddingLeft: '11px',
    margin: 0
  }
}));

const PlaySourcesTable = ({ playSourceSearch, showNoDisplayTitleSources, multiEditEnabled, 
  checkedItems, setCheckedItems, setItems, items }) => {
  const styles = useStyles();
  const abortableFetch = useAbortableFetch();
  const { showSuccessAlert, showErrorAlert } = useSnackbarAlert();
  const [playSources, setPlaySources] = useState([]);
  const [selectedPlaySource, setSelectedPlaySource] = useState(null);
  const [itemsCount, setItemsCount] = useState(0);
  const [page, setPage] = useState(0);
  const [offset, setOffset] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(CONSUMPTION_DATA_DEFAULT_ROWS_PER_PAGE);
  const [rowsPerPageOptions] = useState(CONSUMPTION_DATA_ROWS_PER_PAGE_OPTIONS);
  const [editingItem, setEditingItem] = useState(null);
  const [editValue, setEditValue] = useState('');

  useEffect(() => {
    fetchData();
  }, [offset, rowsPerPage, playSourceSearch, showNoDisplayTitleSources]);

  useEffect(() => {
    setOffset(page * rowsPerPage);
  }, [page, rowsPerPage]);

  const fetchData = async () => {
    try {
      const fetchedData = await abortableFetch('GET',
        '/api/consumption-data', {
        query: {  
          offset,
          limit: rowsPerPage,
          filter: playSourceSearch,
          showNoDisplayTitleSources
        }
      });
    setItems(fetchedData?.items || []);
    setItemsCount(fetchedData?.total || 0);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchPlaySources = async () => {
    try {
      const fetchedData = await abortableFetch('GET', '/api/consumption-data/play-sources');
      setPlaySources(fetchedData || []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleAdd = (item) => {
    setEditingItem(item.id);
    setEditValue(null);
    setSelectedPlaySource(null);
    fetchPlaySources();
  };

  const handleEdit = (item) => {
    setEditingItem(item.id);
    setEditValue(item.displayTitle || '');
    
    fetchPlaySources().then(() => {
      if (item.displayTitle) {
        setSelectedPlaySource(null);
      }
    });
  };

  const handleSave = async (playSourceId) => {
    try {

      let newDisplayTitle;

      if(selectedPlaySource?.isNew) {
        newDisplayTitle = selectedPlaySource.name;
      }
      else {
        newDisplayTitle = playSources.find(source => source.id === selectedPlaySource)?.name;
      }
      
      await abortableFetch('PUT', `/api/consumption-data`, {
        body: { ids: [playSourceId], displayTitle: newDisplayTitle }
      });
      
      const isFirstDisplayTitle = items.find(item => item.id === playSourceId && item.displayTitle === null);

      if (isFirstDisplayTitle) {
        setItems(items.filter(item => item.id !== playSourceId));
      } else {
        setItems(items.map(item => 
          item.id === playSourceId ? { ...item, displayTitle: newDisplayTitle } : item
        ));
      }
      
      setEditingItem(null);
      setEditValue(null);
      setSelectedPlaySource(null);
      showSuccessAlert('Display play source updated successfully');
    } catch (error) {
      console.error('Error updating display title:', error);
      showErrorAlert('Error updating display title');
    }
  };

  const handleCancel = () => {
    setEditingItem(null);
    setEditValue(null);
    setSelectedPlaySource(null);
  };

  const handleCheckboxChange = (itemId) => {
    setCheckedItems(prev => {
      const newChecked = new Set(prev);
      if (newChecked.has(itemId)) {
        newChecked.delete(itemId);
      } else {
        newChecked.add(itemId);
      }
      return newChecked;
    });
  };

  const handleSelectAll = () => {
    setCheckedItems(prev => {
      if (prev.size === items.length) {
        return new Set();
      } else {
        return new Set(items.map(item => item.id));
      }
    });
  };

  return (
    <div>
      <div className={styles.consumptionDataContainer}>
        <div>
          {/* Header */}
          <div className={styles.tableHeadContainer}>
            <TableRow className={styles.tableRow}>
              {multiEditEnabled ? (
                <BrandCheckbox
                  className={styles.tableMultiEditCheckbox}
                  checked={checkedItems.size === items.length && items.length > 0}
                  indeterminate={checkedItems.size > 0 && checkedItems.size < items.length}
                  onChange={handleSelectAll}
                />
              ) : null}
              <TableCell className={styles.tableCell}>
                Original Title
              </TableCell>
              <TableCell className={styles.tableCell}>
                Display Title
              </TableCell>
              <TableCell className={styles.tableCellLast}></TableCell>
            </TableRow>
          </div>
          
          {/* Body */}
          <div style={{ width: '100%', maxWidth: '100%' }}>
            {items.map((item) => (
              <div key={item.id} className={styles.rowWrapper} style={{ width: '100%' }}>
                <div className={`${styles.tableRowBody} ${checkedItems.has(item.id) ? styles.tableRowBodyChecked : ''}`}>
                {multiEditEnabled ? (
                  <BrandCheckbox 
                    className={styles.tableMultiEditCheckbox}
                    checked={checkedItems.has(item.id)}
                    onChange={() => handleCheckboxChange(item.id)}
                  />
                ) : null}
                  <div className={styles.tableCellBody}>
                    <BrandPopover
                      popoverData={item.originalTitle}
                      popoverDataFormated={
                        <div className={styles.truncatedText}>
                          {item.originalTitle}
                        </div>
                      }
                    />
                  </div>

                  {editingItem === item.id ? 
                    <div className={styles.tableCellBodyEdit}>
                    <div className={styles.autoCompleteContainer}>
                          <div className={styles.halfWidthAutocomplete}>
                            <BrandAutocomplete
                              placeholder={'Type your display title here'}
                              autoCompleteOptions={playSources}
                              selectedAutoCompleteOption={selectedPlaySource}
                              onSelectedAutoCompleteOption={setSelectedPlaySource}
                              allowNewOptions={true}
                            />
                          </div>
                          <div className={styles.actionBtnContainer}>
                            <div className={styles.saveIcon}
                              onClick={() => handleSave(item.id)}>
                              <DoneIcon fontSize="medium" />
                            </div>
                            <div className={styles.cancelIcon}
                              onClick={handleCancel}>
                              <CloseIcon fontSize="medium" />
                            </div>
                          </div>
                        </div>                   
                    </div>
                    :
                    <div className={styles.tableCellBody}>
                      {item.displayTitle ? 
                        <div className={styles.tableCellBody}>
                          <BrandPopover
                            popoverData={item.displayTitle}
                            popoverDataFormated={
                              <div className={styles.truncatedText}>
                                {item.displayTitle}
                              </div>
                            }
                          />
                        </div>
                      : 
                        multiEditEnabled ? (
                          <BrandTooltip 
                            type={TooltipType.error}
                            title="Single add can't be used in multi-edit mode">
                            <BrandButton size={ButtonSize.SMALL} 
                              onClick={() => handleAdd(item)}
                              disabled={multiEditEnabled}
                            >
                              <AddRoundedIcon fontSize="medium" /> Add
                            </BrandButton>
                          </BrandTooltip>
                        ) : (
                          <BrandButton size={ButtonSize.SMALL} 
                            onClick={() => handleAdd(item)}>
                            <AddRoundedIcon fontSize="medium" /> Add
                          </BrandButton>
                        )
                      }
                    </div>
                  }

                  <div className={styles.iconContainer}>
                    {editingItem === item.id ? null : (
                      item.displayTitle ? (
                        multiEditEnabled ? (
                          <BrandTooltip 
                            type={TooltipType.error}
                            title="Single edit can't be used in multi-edit mode">
                            <div 
                              className={styles.disabledActionBtn}>
                              <EditOutlinedIcon fontSize="medium" />
                            </div>
                          </BrandTooltip>
                        ) : (
                          <div 
                            className={styles.actionBtn}
                            onClick={() => handleEdit(item)}>
                            <EditOutlinedIcon fontSize="medium" />
                          </div>
                        )
                      ) : null
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <TablePagination
        className={styles.tablePagination}
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        count={itemsCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => handleRowsPerPageChange(event, offset, setRowsPerPage, setPage, setOffset)}
        ActionsComponent={TablePaginationActions}
      />
    </div>
  );
};

export default PlaySourcesTable;
